import { useEffect, useState, useCallback } from 'react';
import { User } from 'firebase/auth';
import { authService } from '../services/auth.service';
import { UserData } from '../contexts/AuthContext';

interface UseBackendAuthResult {
  userProfile: UserData | null;
  idToken: string | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useBackendAuth = (user: User | null): UseBackendAuthResult => {
  const [userProfile, setUserProfile] = useState<UserData | null>(null);
  const [idToken, setIdToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchUserProfile = useCallback(async () => {
    if (!user) {
      setUserProfile(null);
      setIdToken(null);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = await user.getIdToken();
      setIdToken(token);

      // Verify token and get user profile in one call
      const userData = await authService.verifyToken(token);

      if (userData && userData.uid) {
        setUserProfile(userData);
      } else {
        setError('Failed to authenticate with backend');
        setUserProfile(null);
      }
    } catch (error) {
      setError('Failed to authenticate with backend');
      setUserProfile(null);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchUserProfile();
  }, [fetchUserProfile]);

  return {
    userProfile,
    idToken,
    loading,
    error,
    refetch: fetchUserProfile,
  };
};
